import React, { memo, useCallback, useRef } from 'react';
import { IconCheck, Pagination } from '@americanexpress/dls-react';
import { useHistory, useLocation } from 'react-router-dom';

import { useVisibleAccounts } from 'src/hooks/queries/accounts';

import Anchor from 'src/components/atomic/atoms/anchor/Anchor';
import EmptyStateBox from 'src/components/empty-state-box/EmptyStateBox';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';

import LiteralsService from 'src/services/literals/LiteralsService';
import { TransactionsTableViewModel } from 'src/types/view-models/transactions/transactions-table.view.model';
import { TransactionModel } from 'src/types/transaction.model';

import styles from './transactionTable.module.scss';
import { useCategories } from 'src/hooks/queries/categories';
import { useIncomes } from 'src/hooks/queries/incomes';
import Circle from '../../atoms/circle/Circle';
import { DLSSize } from 'src/types/dls-size.enum';
import { TransactionTableContent } from 'src/components/atomic/organisms/transaction/TransactionTableContent';
import Loading from '../../atoms/loading/Loading';
import { goToTransactions } from 'src/services/router.service';
import { useUser } from 'src/hooks/queries/users';
import { UserUtils } from 'src/types/user.model';
import { trackClick } from 'src/hooks/useAdobeTags';
import adobeTags from 'src/types/adobe-tags';
import Selectors from 'src/utils/selectors';
import { useSearchParams } from 'src/hooks/useSearchParams';
import { BudgetPages } from 'src/components/atomic/pages/BudgetPages';
import { Form } from 'formik';
import { useSelectedTransactionsContext } from 'src/hooks/useSelectedTransactions';

export interface TransactionRowModel {
    transaction: TransactionModel;
    checked: boolean;
}

const TransactionsTable = ({
    transactionRows,
    setTransactionRows,
    searchReset,
    isReview = false,
    totalPages
}: TransactionsTableViewModel): JSX.Element => {
    const { data: user } = useUser();
    const { isLoading: isLoadingVisibleAccounts } = useVisibleAccounts();
    const { isLoading: isLoadingCategories, data: categories } = useCategories();
    const { isLoading: isLoadingIncomes } = useIncomes();
    const isLoading: boolean = isLoadingVisibleAccounts || isLoadingCategories || isLoadingIncomes;
    const { pathname } = useLocation();
    const history = useHistory();
    const formRef = useRef<HTMLFormElement>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const page = parseInt(searchParams.get('page')) || 1;
    const searchValue = searchParams.get('search');
    const isFullPlanUser: boolean = !UserUtils.isBudgetingUser(user);
    const { setAllTransactionsSelect } = useSelectedTransactionsContext();

    const resetFilters = (): void => {
        setSearchParams('');
    };

    /** Sets the new page number when a page number in the pagination is clicked, then scrolls up to the top of the page
     * and focuses on the first element of the table */
    const handlePageChange = (pageNumber: number): void => {
        if (pageNumber === page + 1) {
            trackClick(pathname, adobeTags.Transactions.Pagination.Next);
        } else if (pageNumber === page - 1) {
            trackClick(pathname, adobeTags.Transactions.Pagination.Previous);
        } else {
            trackClick(pathname, `${adobeTags.Transactions.Pagination.Number}>${pageNumber}`);
        }
        setAllTransactionsSelect(false);
        if (!isLoading) {
            searchParams.set('page', pageNumber.toString());
            setSearchParams(searchParams);
            document.getElementById('scrollContainer').scroll(0, 0);
            const firstElement: HTMLElement = formRef.current?.querySelector('[name]:not([disabled])');
            firstElement?.focus();
        }
    };

    /** Sets the new transaction rows of the table when a single row has been checked or unchecked */
    const handleRowCheckChange = useCallback(
        (event: any, row: { id: string; checked: boolean }): void => {
            trackClick(pathname, adobeTags.Transactions.Row[row.checked ? 'Unselect' : 'Select']);
            if (!isLoading) {
                const rowIndex: number = Number(row.id.split('-')[1]);
                const transactionRow: TransactionRowModel = transactionRows[rowIndex];
                const newTransactionRow: TransactionRowModel = {
                    transaction: transactionRow.transaction,
                    checked: !transactionRow.checked
                };
                const newTransactionRows: TransactionRowModel[] = transactionRows.slice();
                newTransactionRows[rowIndex] = newTransactionRow;
                setTransactionRows(newTransactionRows);
            }
        },
        [isLoading, transactionRows]
    );

    if (isLoading) return <Loading lg />;

    return (
        <div id="scroller">
            <Form ref={formRef} className={styles.transactionsTableForm}>
                <TransactionTableContent
                    categories={categories}
                    transactionRows={transactionRows}
                    handleRowCheckChange={handleRowCheckChange}
                    isReview={isReview}
                />
                {transactionRows?.length ? (
                    <Pagination
                        className={styles.transactionsTablePagination}
                        onChange={handlePageChange}
                        data-cy={Selectors.Transactions.PAGINATION}
                        selected={page}
                        total={totalPages}
                    />
                ) : isReview ? (
                    searchValue ? (
                        <EmptyStateBox className={styles.transactionsTableEmptyStateBox}>
                            <div>
                                <Typography
                                    component="p"
                                    variant={TypographyVariants.BODY_BOLD}
                                    aria-live="polite"
                                    data-cy={Selectors.Transactions.EMPTY_STATE_BOX_DESC}
                                >
                                    {LiteralsService.getLiteral(
                                        'TransactionsTable.emptyState.noTransactionsMatchSearch'
                                    )}
                                </Typography>
                                <Anchor
                                    typographyProps={{ component: 'p', variant: TypographyVariants.LINK }}
                                    onClick={searchReset}
                                >
                                    {LiteralsService.getLiteral('TransactionsTable.emptyState.clearSearch')}
                                </Anchor>
                            </div>
                        </EmptyStateBox>
                    ) : (
                        <div className={styles.reviewEmpty} data-cy={Selectors.Transactions.REVIEW_EMPTY_STATE}>
                            <Circle className={styles.circleCheck}>
                                <IconCheck size={DLSSize.LARGE} />
                            </Circle>
                            <Typography component="p" variant={TypographyVariants.HEADING_S}>
                                {LiteralsService.getLiteral('TransactionsTable.emptyState.youreUpToDate')}
                            </Typography>
                            <Typography component="p" variant={TypographyVariants.BODY}>
                                {LiteralsService.getLiteral(
                                    'TransactionsTable.emptyState.youHaveNoTransactionsToReview'
                                )}
                            </Typography>
                            <Anchor
                                typographyProps={{ component: 'p', variant: TypographyVariants.LINK }}
                                data-cy={Selectors.Transactions.VIEW_ALL_TRANSACTIONS}
                                onClick={() => goToTransactions(isFullPlanUser, history)}
                                tag={adobeTags.Transactions.ReviewComplete}
                                href={BudgetPages.TRANSACTIONS}
                            >
                                {LiteralsService.getLiteral('Insights.newTransactionsRecap.viewTransactions')}
                            </Anchor>
                        </div>
                    )
                ) : (
                    <EmptyStateBox className={styles.transactionsTableEmptyStateBox}>
                        <div>
                            <Typography
                                component="p"
                                variant={TypographyVariants.BODY_BOLD}
                                aria-live="polite"
                                data-cy={Selectors.Transactions.EMPTY_STATE_BOX_DESC}
                            >
                                {searchValue
                                    ? LiteralsService.getLiteral(
                                          'TransactionsTable.emptyState.noTransactionsMatchSearch'
                                      )
                                    : LiteralsService.getLiteral('TransactionsTable.emptyState.noTransactions')}
                            </Typography>
                            <Anchor
                                typographyProps={{ component: 'p', variant: TypographyVariants.LINK }}
                                onClick={() => (searchValue ? searchReset() : resetFilters())}
                                tag={searchValue ? undefined : adobeTags.Filter.Clear}
                            >
                                {searchValue
                                    ? LiteralsService.getLiteral('TransactionsTable.emptyState.clearSearch')
                                    : LiteralsService.getLiteral('TransactionsTable.emptyState.clearFilters')}
                            </Anchor>
                        </div>
                    </EmptyStateBox>
                )}
            </Form>
        </div>
    );
};

export default memo(TransactionsTable);
